import React, { useState } from 'react';
import { BaseCRUDPage } from '../common';
import { Input, RequiredIndicator, Column, FieldError } from '../../components';
import { portService } from '../../services';
import { Port, PortCreate, PortUpdate } from '../../types';

interface PortFormData {
  code: string;
  port: string;
  country: string;
  terminal: string;
  address: string;
  remarks: string;
}

const PortsPage: React.FC = () => {
  const [formData, setFormData] = useState<PortFormData>({
    code: '',
    port: '',
    country: '',
    terminal: '',
    address: '',
    remarks: ''
  });

  // Define table columns
  const columns: Column[] = [
    { key: 'code', label: 'Code', sortable: true, searchable: true },
    { key: 'port', label: 'Port', sortable: true, searchable: true },
    { key: 'country', label: 'Country', sortable: true, searchable: true },
    { key: 'terminal', label: 'Terminal', sortable: true, searchable: true },
    { key: 'address', label: 'Address', sortable: true, searchable: true },
    { key: 'remarks', label: 'Remarks', sortable: true, searchable: true }
  ];

  // Form fields component with error support
  const formFields = (fieldErrors: Record<string, string>) => (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Port Code <RequiredIndicator />
        </label>
        <Input
          type="text"
          value={formData.code}
          onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
          required
          placeholder="e.g., USNYC"
        />
        <FieldError error={fieldErrors.code} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Port Name <RequiredIndicator />
        </label>
        <Input
          type="text"
          value={formData.port}
          onChange={(e) => setFormData(prev => ({ ...prev, port: e.target.value }))}
          required
          placeholder="e.g., Port of New York"
        />
        <FieldError error={fieldErrors.port} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Country <RequiredIndicator />
        </label>
        <Input
          type="text"
          value={formData.country}
          onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
          required
          placeholder="e.g., United States"
        />
        <FieldError error={fieldErrors.country} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Terminal
        </label>
        <Input
          type="text"
          value={formData.terminal}
          onChange={(e) => setFormData(prev => ({ ...prev, terminal: e.target.value }))}
          placeholder="e.g., Terminal A"
        />
        <FieldError error={fieldErrors.terminal} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Address
        </label>
        <textarea
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          value={formData.address}
          onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
          placeholder="Port address"
          rows={2}
        />
        <FieldError error={fieldErrors.address} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Remarks
        </label>
        <Input
          type="text"
          value={formData.remarks}
          onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
          placeholder="Additional notes"
        />
        <FieldError error={fieldErrors.remarks} />
      </div>
    </div>
  );

  // Modal icon
  const modalIcon = (
    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
      <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    </div>
  );

  return (
    <BaseCRUDPage<Port, PortCreate, PortUpdate>
      title="Port Management"
      entityName="port"
      columns={columns}
      service={portService}
      formFields={formFields}
      formData={formData}
      setFormData={setFormData}
      requiredFields={['code', 'port', 'country']}
      getInitialFormData={() => ({ code: '', port: '', country: '', terminal: '', address: '', remarks: '' })}
      toCreateData={(formData) => ({
        code: formData.code,
        port: formData.port,
        country: formData.country,
        terminal: formData.terminal || undefined,
        address: formData.address || undefined,
        remarks: formData.remarks || undefined
      })}
      toUpdateData={(formData, original) => {
        const updateData: PortUpdate = {};
        if (formData.code !== original.code) updateData.code = formData.code;
        if (formData.port !== original.port) updateData.port = formData.port;
        if (formData.country !== original.country) updateData.country = formData.country;
        if (formData.terminal !== (original.terminal || '')) updateData.terminal = formData.terminal || null;
        if (formData.address !== (original.address || '')) updateData.address = formData.address || null;
        if (formData.remarks !== (original.remarks || '')) updateData.remarks = formData.remarks || null;
        return updateData;
      }}
      toFormData={(port) => ({
        code: port.code,
        port: port.port,
        country: port.country,
        terminal: port.terminal || '',
        address: port.address || '',
        remarks: port.remarks || ''
      })}
      modalIcon={modalIcon}
      modalTitle={(isEdit) => ({
        title: isEdit ? 'Edit Port' : 'Create New Port',
        subtitle: isEdit ? 'Update port information' : 'Add a new shipping port to the system'
      })}
      customDeleteConfirm={(port) => `Are you sure you want to delete port "${port.port}"?`}
      defaultSearchColumn="code"
    />
  );
};

export default PortsPage;
